import {
  DropdownMenuCheckboxItemProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
} from '@radix-ui/react-dropdown-menu';
import {TooltipContentProps} from '@radix-ui/react-tooltip';
import {Button, ButtonProps} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import React, {useState} from 'react';

type OverflowMenuProps = {
  'aria-label': string;
  children: React.ReactNode;
  contentProps?: DropdownMenuContentProps;
  'data-test'?: string;
  isDisabled?: boolean;
  isRootModal?: boolean;
  tooltip?: string;
  variant?: ButtonProps['variant'];
};

export const OverflowMenu = ({
  'aria-label': ariaLabel,
  children,
  contentProps = {},
  'data-test': dataTest,
  isDisabled,
  isRootModal,
  tooltip,
  variant,
}: OverflowMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <Tooltip.Provider>
      <DropdownMenu.Root
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        modal={isRootModal}>
        <DropdownMenu.Trigger data-test={dataTest} asChild>
          <Button
            icon="overflow-horizontal"
            variant={variant ?? 'ghost'}
            active={isMenuOpen}
            disabled={isDisabled}
            aria-label={ariaLabel}
            tooltip={tooltip}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            data-test="nw-overflow-menu-content"
            className="z-[2] shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]"
            align="end"
            sideOffset={12}
            onCloseAutoFocus={e => e.preventDefault()}
            {...contentProps}>
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Tooltip.Provider>
  );
};

type OverflowMenuItemProps = {
  displayName: string;
  icon: IconName;
  tooltipContent?: React.ReactElement | string;
  tooltipProps?: TooltipContentProps;
} & DropdownMenuItemProps;

export const OverflowMenuItem = ({
  displayName,
  disabled,
  icon,
  tooltipContent,
  tooltipProps = {},
  ...dropdownItemProps
}: OverflowMenuItemProps) => {
  return (
    <DropdownMenu.Item
      className="radix-disabled:pointer-events-auto radix-disabled:cursor-not-allowed"
      disabled={disabled}
      {...dropdownItemProps}>
      <div className="flex w-full items-center justify-between gap-16">
        <div className="flex items-center gap-8">
          <Icon className="shrink-0" name={icon} role="presentation" />
          <span>{displayName}</span>
        </div>
        {tooltipContent && (
          <div className="night-aware flex">
            <Button
              className={disabled ? 'opacity-60' : ''}
              variant="ghost"
              icon="info"
              size="small"
              tooltip={tooltipContent}
              tooltipProps={{
                className: 'w-[16rem]',
                side: 'right',
                sideOffset: 20,
                ...tooltipProps,
              }}
            />
          </div>
        )}
      </div>
    </DropdownMenu.Item>
  );
};

export const OverflowMenuCheckboxItem = ({
  displayName,
  icon,
  ...dropdownCheckboxItemProps
}: {
  displayName: string;
  icon: IconName;
} & DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownMenu.CheckboxItem
      className="flex justify-between text-moon-800 dark:text-moon-300"
      {...dropdownCheckboxItemProps}>
      <div className="flex items-center gap-8">
        <Icon className="shrink-0" name={icon} />
        {displayName}
      </div>
      <DropdownMenu.ItemIndicator>
        <Icon name="checkmark" width={18} height={18} />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
  );
};

/**
 * Create a wrapped OverflowMenuItem component with preconfigured props.
 * Use this to configure consistent props across all items within a menu.
 */
export const makeOverflowMenuItem =
  ({
    tooltipProps: outerTooltipProps,
    ...outerProps
  }: Partial<OverflowMenuItemProps> = {}) =>
  ({
    tooltipProps: innerTooltipProps = {},
    ...innerProps
  }: OverflowMenuItemProps) => {
    return (
      <OverflowMenuItem
        tooltipProps={{
          ...outerTooltipProps,
          ...innerTooltipProps,
        }}
        {...outerProps}
        {...innerProps}
      />
    );
  };
